import React from 'react';
import { BANK_LOGO_DATA_URIS } from './bankLogoAssets';

export type BankId =
  | 'kbank'
  | 'scb'
  | 'bbl'
  | 'ktb'
  | 'ttb'
  | 'bay'
  | 'gsb'
  | 'baac'
  | 'cimb'
  | 'uob'
  | 'tisco'
  | 'kkp'
  | 'lhbank'
  | 'isbt'
  | 'ghb';

interface BankInfo {
  id: BankId;
  name: string;
  shortName: string;
  bgColor: string;
  fgColor: string;
  logoUrl?: string;
}

export const THAI_BANKS: Record<BankId, BankInfo> = {
  kbank:  { id: 'kbank',  name: 'ธนาคารกสิกรไทย',        shortName: 'KBank',    bgColor: '#138F2D', fgColor: '#FFFFFF', logoUrl: BANK_LOGO_DATA_URIS.kbank },
  scb:    { id: 'scb',    name: 'ธนาคารไทยพาณิชย์',        shortName: 'SCB',      bgColor: '#4E2A84', fgColor: '#F2C12E', logoUrl: BANK_LOGO_DATA_URIS.scb },
  bbl:    { id: 'bbl',    name: 'ธนาคารกรุงเทพ',           shortName: 'BBL',      bgColor: '#1E4598', fgColor: '#FFFFFF', logoUrl: BANK_LOGO_DATA_URIS.bbl },
  ktb:    { id: 'ktb',    name: 'ธนาคารกรุงไทย',            shortName: 'KTB',      bgColor: '#00A0E2', fgColor: '#FFFFFF', logoUrl: BANK_LOGO_DATA_URIS.ktb },
  ttb:    { id: 'ttb',    name: 'ธนาคารทหารไทยธนชาต',        shortName: 'ttb',      bgColor: '#FFFFFF', fgColor: '#1E4DA1', logoUrl: BANK_LOGO_DATA_URIS.ttb },
  bay:    { id: 'bay',    name: 'ธนาคารกรุงศรีอยุธยา',         shortName: 'Krungsri', bgColor: '#FEC43B', fgColor: '#4A2525', logoUrl: BANK_LOGO_DATA_URIS.bay },
  gsb:    { id: 'gsb',    name: 'ธนาคารออมสิน',              shortName: 'GSB',      bgColor: '#EB008B', fgColor: '#FFFFFF', logoUrl: BANK_LOGO_DATA_URIS.gsb },
  baac:   { id: 'baac',   name: 'ธ.ก.ส.',                   shortName: 'BAAC',     bgColor: '#0F7B3F', fgColor: '#FFFFFF' },
  cimb:   { id: 'cimb',   name: 'ธนาคารซีไอเอ็มบีไทย',       shortName: 'CIMB',     bgColor: '#7B181C', fgColor: '#FFFFFF' },
  uob:    { id: 'uob',    name: 'ธนาคารยูโอบี',              shortName: 'UOB',      bgColor: '#005EB8', fgColor: '#FFFFFF' },
  tisco:  { id: 'tisco',  name: 'ธนาคารทิสโก้',              shortName: 'TISCO',    bgColor: '#003B71', fgColor: '#FFFFFF' },
  kkp:    { id: 'kkp',    name: 'ธนาคารเกียรตินาคินภัทร',      shortName: 'KKP',      bgColor: '#003366', fgColor: '#FFC629' },
  lhbank: { id: 'lhbank', name: 'แลนด์ แอนด์ เฮ้าส์',         shortName: 'LH Bank',  bgColor: '#7B2D8E', fgColor: '#FFFFFF' },
  isbt:   { id: 'isbt',   name: 'ธนาคารอิสลาม',              shortName: 'ISBT',     bgColor: '#006847', fgColor: '#FFD700' },
  ghb:    { id: 'ghb',    name: 'ธนาคารอาคารสงเคราะห์',       shortName: 'GHB',      bgColor: '#F37021', fgColor: '#FFFFFF' },
};

export const BANK_OPTIONS: { value: BankId; label: string }[] = (Object.keys(THAI_BANKS) as BankId[]).map((id) => ({
  value: id,
  label: `${THAI_BANKS[id].shortName} - ${THAI_BANKS[id].name}`,
}));

interface BankLogoProps {
  bank: BankId;
  size?: number;
  className?: string;
}

/**
 * Renders a circular bank logo using bundled real bank logo data URIs,
 * falling back to a brand-colored SVG badge for other banks.
 */
export const BankLogo: React.FC<BankLogoProps> = ({ bank, size = 48, className = '' }) => {
  const info = THAI_BANKS[bank] || THAI_BANKS.kbank;
  const px = `${size}px`;

  if (info.logoUrl) {
    return (
      <div
        className={`flex items-center justify-center rounded-full overflow-hidden flex-shrink-0 bg-white ${className}`}
        style={{ width: px, height: px }}
        title={info.name}
      >
        <img
          src={info.logoUrl}
          alt={info.name}
          width={size}
          height={size}
          decoding="async"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
    );
  }

  return (
    <div
      className={`flex items-center justify-center rounded-full overflow-hidden flex-shrink-0 shadow-inner ${className}`}
      style={{
        width: px,
        height: px,
        backgroundColor: info.bgColor,
        border: '1.5px solid rgba(255,255,255,0.28)',
      }}
      title={info.name}
    >
      <BankIcon bank={bank} fg={info.fgColor} size={size} />
    </div>
  );
};

const BANK_LABEL: Record<BankId, { text: string; fontSize: number }> = {
  kbank:  { text: 'K+',    fontSize: 16 },
  scb:    { text: 'SCB',   fontSize: 13 },
  bbl:    { text: 'BBL',   fontSize: 13 },
  ktb:    { text: 'KTB',   fontSize: 13 },
  ttb:    { text: 'ttb',   fontSize: 14 },
  bay:    { text: 'BAY',   fontSize: 13 },
  gsb:    { text: 'GSB',   fontSize: 13 },
  baac:   { text: 'ธกส',   fontSize: 12 },
  cimb:   { text: 'CIMB',  fontSize: 11 },
  uob:    { text: 'UOB',   fontSize: 13 },
  tisco:  { text: 'TISCO', fontSize: 9  },
  kkp:    { text: 'KKP',   fontSize: 13 },
  lhbank: { text: 'LH',    fontSize: 15 },
  isbt:   { text: 'iBank', fontSize: 10 },
  ghb:    { text: 'ธอส',   fontSize: 12 },
};

const BankIcon: React.FC<{ bank: BankId; fg: string; size: number }> = ({ bank, fg, size }) => {
  const label = BANK_LABEL[bank] || BANK_LABEL.kbank;
  const scale = size / 48;
  return (
    <svg
      width={size * 0.84}
      height={size * 0.84}
      viewBox="0 0 24 24"
      style={{ overflow: 'visible' }}
    >
      <text
        x="12"
        y="12"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        fontWeight={900}
        fontSize={(label.fontSize * scale).toFixed(1)}
        fill={fg}
        letterSpacing="-0.4"
      >
        {label.text}
      </text>
    </svg>
  );
};
